import { useEffect, useState } from 'react'
import to from 'await-catch'
import moment from 'moment'
import { getAvailableAppointments } from '../api'
import { cleanParams } from '../utils/cleanParams'
import { formatTimeToHHmm, formatTimeToHHmmss } from '../utils/formatDate'

const getSpecialtiesToSearch = filters => {
	if (Array.isArray(filters?.specialities) && filters.specialities.length > 0) {
		return filters.specialities
	}

	return filters?.speciality ? [filters.speciality] : []
}

const buildParams = ({ patient, filters, specialityId }) => {
  const fechaDesde = (filters.dateFrom ? moment(filters.dateFrom) : moment()).format('YYYY-MM-DD')
  const fechaHasta = (filters.dateTo ? moment(filters.dateTo) : moment().add(60, 'days')).format('YYYY-MM-DD')

	let horaDesde, horaHasta
	if (filters?.hourFrom && filters?.hourTo) {
		horaDesde = formatTimeToHHmm(filters.hourFrom)
		horaHasta = formatTimeToHHmm(filters.hourTo)
	} else {
		horaDesde = '00:00'
		horaHasta = '23:59'
	}

	const clinicaParam = Array.isArray(filters?.clinic)
		? filters.clinic.filter(Boolean).join(',')
		: filters?.clinic

	return cleanParams({
		idObraSocial: patient.idObraSocial,
		idPlan: patient.idPlan,
		clinicas: clinicaParam,
		idEspecialidad: specialityId,
		idProfesional: filters?.professional,
		fechaDesde,
		fechaHasta,
		horaDesde,
		horaHasta,
		idSistema: ''
	})
}

/**
 * Obtiene los turnos disponibles para los filtros indicados y los agrupa por
 * profesional + clínica. Vuelve a buscar cada vez que cambia `trigger`.
 */
export const useAvailableAppointments = ({
	patient,
	filters,
	trigger,
	enabled = true,
	showLoader,
	hideLoader,
	addFlashMessage
}) => {
	const [appointments, setAppointments] = useState([])
	const [hasFetched, setHasFetched] = useState(false)

	useEffect(() => {
		if (!enabled) {
			setHasFetched(false)
			return
		}

		const fetchTurnos = async () => {
			setHasFetched(false)
			try {
				showLoader()

				const specialitiesToSearch = getSpecialtiesToSearch(filters)

				if (specialitiesToSearch.length === 0) {
					setAppointments([])
					return
				}

				const responses = await Promise.all(
					specialitiesToSearch.map(specialityId => {
						const params = buildParams({ patient, filters, specialityId })
						return to(getAvailableAppointments(params))
					})
				)

				const hadAnyError = responses.some(([err]) => !!err)
				const mergedTurnos = responses.flatMap(([, data]) =>
					Array.isArray(data?.data)
						? data.data.flatMap(dayGroup =>
								Array.isArray(dayGroup?.turnos) ? dayGroup.turnos : []
						  )
						: []
				)

				const dedupedTurnos = Array.from(
					new Map(mergedTurnos.map(turn => [turn.idTurno, turn])).values()
				)

				if (hadAnyError) {
					addFlashMessage('Ocurrió un error cargando algunos turnos')
				}

				setAppointments(dedupedTurnos)
				setHasFetched(true)
			} catch (error) {
				console.error('Error en fetchTurnos:', error)
				addFlashMessage('Ocurrió un error cargando los turnos')
				setAppointments([])
				setHasFetched(true)
			} finally {
				hideLoader()
			}
		}

		fetchTurnos()
		// Solo re-buscamos cuando se dispara una nueva búsqueda (trigger).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [trigger, enabled])

	return { appointments, hasFetched }
}
